import { state } from './state.js';
import { escapeHtml, formatDateTime } from './utils/formatters.js';
import { showToast } from './utils/toast.js';
import { updateBatchProgressModal } from './controllers/batch.controller.js';
import { renderJobsTable } from './controllers/logs.controller.js';
import { updateStagingDeployProgress, addStagingDeployLog } from './controllers/staging.controller.js';

export type SSEEventHandler = (data: any, eventName: string) => void;

export class SSEClient {
  private eventSource: EventSource | null = null;
  private url: string;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private reconnectAttempts = 0;
  private maxReconnectDelay = 30000;
  private isExplicitlyClosed = false;
  private listeners: Map<string, Set<SSEEventHandler>> = new Map();

  constructor(url: string = '/api/v1/stream') {
    this.url = url;
  }

  public connect(): void {
    if (typeof window === 'undefined' || typeof EventSource === 'undefined') {
      return;
    }

    if (this.eventSource && (this.eventSource.readyState === EventSource.OPEN || this.eventSource.readyState === EventSource.CONNECTING)) {
      return;
    }

    this.isExplicitlyClosed = false;
    this.updateStatusUI('connecting');

    try {
      this.eventSource = new EventSource(this.url);

      this.eventSource.onopen = () => {
        this.reconnectAttempts = 0;
        this.updateStatusUI('connected');
      };

      this.eventSource.onerror = () => {
        this.updateStatusUI('disconnected');
        if (this.eventSource) {
          this.eventSource.close();
          this.eventSource = null;
        }
        if (!this.isExplicitlyClosed) {
          this.scheduleReconnect();
        }
      };

      // Register standard SSE event types
      const standardEvents = [
        'connected',
        'ping',
        'audit_log',
        'job_created',
        'job_updated',
        'job_progress',
        'job_status',
        'batch_started',
        'batch_progress',
        'batch_stage_completed',
        'batch_completed',
        'batch_failed',
        'webhook_received',
        'webhook_batch',
        'webhook_dispatched',
        'webhook_deadletter',
        'worker_status',
      ];

      for (const evtName of standardEvents) {
        this.eventSource.addEventListener(evtName, (event: MessageEvent) => {
          this.handleEvent(evtName, event);
        });
      }

      this.eventSource.onmessage = (event: MessageEvent) => {
        this.handleEvent('message', event);
      };
    } catch (err) {
      console.warn('[SSE] Failed to initialize EventSource:', err);
      this.scheduleReconnect();
    }
  }

  public disconnect(): void {
    this.isExplicitlyClosed = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    this.updateStatusUI('disconnected');
  }

  public on(eventName: string, handler: SSEEventHandler): () => void {
    if (!this.listeners.has(eventName)) {
      this.listeners.set(eventName, new Set());
    }
    this.listeners.get(eventName)!.add(handler);
    return () => {
      this.listeners.get(eventName)?.delete(handler);
    };
  }

  private handleEvent(eventName: string, event: MessageEvent): void {
    let data: any = event.data;
    try {
      if (typeof event.data === 'string' && (event.data.startsWith('{') || event.data.startsWith('['))) {
        data = JSON.parse(event.data);
      }
    } catch {
      // Keep as string if not valid JSON
    }

    // Default internal event handlers for UI updates
    this.dispatchInternal(eventName, data);

    // Call registered listeners
    const handlers = this.listeners.get(eventName);
    if (handlers) {
      for (const handler of handlers) {
        try {
          handler(data, eventName);
        } catch (err) {
          console.error(`[SSE] Listener error on '${eventName}':`, err);
        }
      }
    }

    const wildcardHandlers = this.listeners.get('*');
    if (wildcardHandlers) {
      for (const handler of wildcardHandlers) {
        try {
          handler(data, eventName);
        } catch (err) {
          console.error(`[SSE] Wildcard listener error on '${eventName}':`, err);
        }
      }
    }
  }

  private dispatchInternal(eventName: string, data: any): void {
    switch (eventName) {
      case 'connected': {
        this.updateStatusUI('connected');
        break;
      }

      case 'audit_log': {
        this.onAuditLogReceived(data);
        break;
      }

      case 'job_created':
      case 'job_updated':
      case 'job_progress':
      case 'job_status': {
        this.onJobUpdated(data, eventName);
        break;
      }

      case 'batch_progress':
      case 'batch_stage_completed': {
        this.onBatchProgressReceived(data);
        break;
      }

      case 'webhook_received':
      case 'webhook_batch':
      case 'webhook_dispatched':
      case 'webhook_deadletter': {
        this.onWebhookEventReceived(data, eventName);
        break;
      }

      case 'worker_status': {
        this.onWorkerStatusReceived(data);
        break;
      }
    }
  }

  private onAuditLogReceived(log: any): void {
    if (!log || typeof log !== 'object') return;

    // 1. Update State
    const existingIdx = state.logs.list.findIndex((l) => l.id === log.id);
    if (existingIdx === -1) {
      state.logs.list.unshift(log);
      state.logs.total++;
    } else {
      state.logs.list[existingIdx] = log;
    }

    // 2. Update Log Stats
    if (state.logs.stats) {
      state.logs.stats.total = (state.logs.stats.total || 0) + (existingIdx === -1 ? 1 : 0);
      const lvl = String(log.level || 'info').toLowerCase() as keyof typeof state.logs.stats;
      if (lvl in state.logs.stats) {
        (state.logs.stats as any)[lvl] = ((state.logs.stats as any)[lvl] || 0) + (existingIdx === -1 ? 1 : 0);
      }
    }

    // 3. Update Badges
    const s = state.logs.stats;
    const elChipAll = document.getElementById('count-chip-all');
    const elSubtabStreamCount = document.getElementById('subtab-stream-count');
    const elTotalLogs = document.getElementById('metric-total-logs');
    if (elChipAll) elChipAll.textContent = String(s.total || 0);
    if (elSubtabStreamCount) elSubtabStreamCount.textContent = String(s.total || 0);
    if (elTotalLogs) elTotalLogs.textContent = String(s.total || 0);

    // 4. Live insert into Audit Stream List if on stream view
    const streamList = document.getElementById('audit-log-stream-list');
    const emptyState = document.getElementById('logs-empty-state');
    if (streamList) {
      if (emptyState) emptyState.style.display = 'none';

      const item = document.createElement('div');
      item.className = 'audit-log-item sse-new-item';
      item.innerHTML = `
        <div class="log-item-header">
          <span class="log-level-badge level-${escapeHtml(log.level)}">${escapeHtml(log.level)}</span>
          <span class="log-timestamp">${formatDateTime(log.created_at || new Date().toISOString())}</span>
          <span class="log-category-tag">[${escapeHtml(log.category)}]</span>
          <span class="log-action-tag">${escapeHtml(log.action)}</span>
          ${log.job_id ? `<span class="job-type-badge" style="cursor: pointer;" onclick="window.AkeneoDashboard && window.AkeneoDashboard.openJobDetail('${escapeHtml(log.job_id)}')">${escapeHtml(log.job_id)}</span>` : ''}
          ${log.entity_type ? `<span class="log-entity-tag">${escapeHtml(log.entity_type)}${log.entity_id ? `:${escapeHtml(log.entity_id)}` : ''}</span>` : ''}
          <span class="log-message-text">${escapeHtml(log.message)}</span>
        </div>
        ${log.details ? `<div class="log-item-details mono">${typeof log.details === 'string' ? escapeHtml(log.details) : escapeHtml(JSON.stringify(log.details, null, 2))}</div>` : ''}
      `;

      streamList.insertBefore(item, streamList.firstChild);

      // Keep max 200 items in DOM to prevent lag
      while (streamList.children.length > 200) {
        streamList.removeChild(streamList.lastChild!);
      }

      const viewer = document.getElementById('audit-stream-viewer');
      const autoScrollToggle = document.getElementById('log-autoscroll-toggle') as HTMLInputElement | null;
      if (viewer && autoScrollToggle && autoScrollToggle.checked) {
        viewer.scrollTop = viewer.scrollHeight;
      }
    }

    // 5. Update Job Detail Modal logs if open for this job
    if (state.jobs.selectedJobId && state.jobs.selectedJobId === log.job_id) {
      const jobModalLogs = document.getElementById('job-modal-logs-list');
      if (jobModalLogs) {
        const item = document.createElement('div');
        item.className = 'audit-log-item';
        item.innerHTML = `
          <div class="log-item-header">
            <span class="log-level-badge level-${escapeHtml(log.level)}">${escapeHtml(log.level)}</span>
            <span class="log-timestamp">${formatDateTime(log.created_at || new Date().toISOString())}</span>
            <span class="log-category-tag">[${escapeHtml(log.category)}]</span>
            <span class="log-action-tag">${escapeHtml(log.action)}</span>
            <span class="log-message-text">${escapeHtml(log.message)}</span>
          </div>
        `;
        jobModalLogs.appendChild(item);
      }
    }

    // 6. Update Batch Modal Logs if open
    if (state.batch.activeJobId && state.batch.activeJobId === log.job_id) {
      const batchLogs = document.getElementById('batch-modal-logs-list');
      if (batchLogs) {
        const item = document.createElement('div');
        item.className = `audit-log-item log-${escapeHtml(log.level)}`;
        item.innerHTML = `
          <span class="log-time">[${new Date(log.created_at || Date.now()).toLocaleTimeString()}]</span>
          <span class="log-message">${escapeHtml(log.message)}</span>
        `;
        batchLogs.appendChild(item);
        batchLogs.scrollTop = batchLogs.scrollHeight;
      }
    }

    // 7. Update Staging Deploy Modal Logs if open
    if (state.staging.deploy.activeJobId && state.staging.deploy.activeJobId === log.job_id) {
      addStagingDeployLog(log);
    } else if (state.staging.deploy.isRunning && log.category === 'sync') {
      addStagingDeployLog(log);
    }
  }

  private onJobUpdated(job: any, _eventName: string): void {
    if (!job || !job.id) return;

    const idx = state.jobs.list.findIndex((j) => j.id === job.id);
    if (idx !== -1) {
      state.jobs.list[idx] = { ...state.jobs.list[idx], ...job };
    } else {
      state.jobs.list.unshift(job);
      state.jobs.total++;
    }

    // If Jobs view is active, re-render jobs table live
    if (state.activeTab === 'logs' && state.logs.activeSubTab === 'jobs') {
      renderJobsTable();
    }

    // If Batch Progress modal is open for this job, update it live
    if (state.batch.activeJobId === job.id) {
      updateBatchProgressModal(job);
    }

    // If Staging Deploy modal is open for this job, update it live
    if (state.staging.deploy.activeJobId === job.id) {
      updateStagingDeployProgress(job);
    }
  }

  private onBatchProgressReceived(data: any): void {
    if (!data) return;

    if (state.batch.activeJobId && (data.jobId === state.batch.activeJobId || !data.jobId)) {
      const bar = document.getElementById('batch-modal-progress-bar');
      const percentEl = document.getElementById('batch-modal-progress-percent');
      const stageEl = document.getElementById('batch-modal-stage-label');
      const countsEl = document.getElementById('batch-modal-counts-text');

      if (bar && data.percentage !== undefined) {
        bar.style.width = `${data.percentage}%`;
      }
      if (percentEl && data.percentage !== undefined) {
        percentEl.textContent = `${data.percentage}%`;
      }
      if (stageEl && data.currentEntityType) {
        stageEl.textContent = `Processing Stage: ${data.currentEntityType} (${data.currentStageIndex || 1}/${data.totalStages || 1})`;
      }
      if (countsEl && data.metrics) {
        countsEl.textContent = `${data.metrics.processed || 0} / ${data.metrics.total || 0} entities processed`;
      }
    }

    if (state.staging.deploy.activeJobId && (data.jobId === state.staging.deploy.activeJobId || !data.jobId)) {
      updateStagingDeployProgress(data);
    }
  }

  private onWebhookEventReceived(data: any, eventName: string): void {
    const feed = document.getElementById('webhook-events-feed');
    if (feed && data) {
      const item = document.createElement('div');
      item.className = 'webhook-event-card sse-new-item';
      const eventType = data.entityType || data.entity_type || eventName;
      const entityId = data.entityId || data.entity_id || data.id || '';
      item.innerHTML = `
        <div class="webhook-event-header">
          <span class="badge badge-accent">${escapeHtml(eventType)}</span>
          <span class="mono">${escapeHtml(entityId)}</span>
          <span class="text-muted" style="font-size: 0.75rem;">${new Date().toLocaleTimeString()}</span>
        </div>
      `;
      feed.insertBefore(item, feed.firstChild);
      while (feed.children.length > 50) {
        feed.removeChild(feed.lastChild!);
      }
    }
  }

  private onWorkerStatusReceived(_data: any): void {
    // Worker status updates
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) return;
    this.reconnectAttempts++;
    const delay = Math.min(1000 * Math.pow(1.5, this.reconnectAttempts - 1), this.maxReconnectDelay);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private updateStatusUI(status: 'connected' | 'connecting' | 'disconnected'): void {
    const liveDot = document.getElementById('live-status-dot');
    const liveText = document.getElementById('live-status-text');

    if (!liveDot || !liveText) return;

    if (status === 'connected') {
      liveDot.className = 'live-dot active sse-active';
      liveText.textContent = 'Live: SSE Connected';
    } else if (status === 'connecting') {
      liveDot.className = 'live-dot connecting';
      liveText.textContent = 'Live: Connecting...';
    } else {
      liveDot.className = 'live-dot';
      liveText.textContent = 'Live: Polling';
    }
  }
}

let defaultSSEClient: SSEClient | null = null;

export function getSSEClient(): SSEClient {
  if (!defaultSSEClient) {
    defaultSSEClient = new SSEClient();
  }
  return defaultSSEClient;
}

export function initSSE(): SSEClient {
  const client = getSSEClient();
  client.connect();
  return client;
}
